import { ExporttocsvService, csvDate, csvCurrency } from '../../services/exporttocsv/exporttocsv.service';
import { CurrencyPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { saveAs } from 'file-saver';
import moment from 'moment';
import {
  BaseResponseFailure,
  BaseResponseSuccess,
} from 'src/app/interfaces/base-response-interface';
import { TeamInterface } from 'src/app/interfaces/team/team.interface';
import {
  formatCurrencyInput,
  validateNumberInput,
  validatePasteInput,
} from '../../helpers/number-input.helper';
import { TeamMember } from '../../interfaces/team/member/member.interface';
import { SpinnerService } from '../../services/spinner/spinner.service';
import { StripeService } from '../../services/stripe/stripe.service';
import { SendInviteResponseData, TeamsService } from '../../services/teams/teams.service';
import { UsersService } from '../../services/users/users.service';
import { SessionService } from '../../services/session/session.service';
import { SnackBarComponent } from '../snackbar/snackbar.component';
import {
  TEAM_MEMBER_REQUIRED_UPLOAD_COLUMNS,
  TEAM_MEMBER_UPLOAD_COLUMNS,
  TEAM_MEMBER_UPLOAD_TEMPLATE_URL,
} from 'src/app/constants/enrollment-upload-columns';
declare var ExcelJS: any;

export function parseMemberEmailList(value: unknown): string[] {
  return `${value || ''}`
    .split(/[\s,;]+/)
    .map((email) => email.trim())
    .filter((email, index, emails) => !!email && emails.indexOf(email) === index);
}

export const memberEmailListValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  const emails = parseMemberEmailList(control.value);
  return emails.length > 0
    && emails.every((email) => Validators.email({ value: email } as AbstractControl) === null)
    ? null
    : { emailList: true };
};

@Component({
    selector: 'app-team-add-members',
    templateUrl: './team-add-members.page.html',
    styleUrls: ['./team-add-members.page.scss'],
    providers: [CurrencyPipe],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TeamAddMembersPageComponent implements OnInit, AfterViewInit {
  @ViewChild('email') emailControl: any;
  @ViewChild('allowance') allowanceControl!: ElementRef<HTMLInputElement>;

  public members: Array<TeamMember> = [];
  public removedMembers: Array<TeamMember> = [];
  public memberView: 'current' | 'removed' = 'current';
  public newMembers: Array<TeamMember> = [];
  public form: any;
  public result: any;
  public uploading = false;
  public currentWellspace!: TeamInterface;
  public isAdmin = this.usersService.isAdmin();
  public isSuperAdmin = false;
  public topupEnabled = false;
  public validateNumber = validateNumberInput;
  public validatePaste = validatePasteInput;
  public readonly teamMemberUploadColumns = TEAM_MEMBER_UPLOAD_COLUMNS;
  public readonly teamMemberUploadColumnList = TEAM_MEMBER_UPLOAD_COLUMNS.join(', ');
  public readonly requiredUploadColumns = TEAM_MEMBER_REQUIRED_UPLOAD_COLUMNS;
  public readonly requiredUploadColumnList = TEAM_MEMBER_REQUIRED_UPLOAD_COLUMNS.join(', ');
  public readonly requiredUploadColumnVerb =
    TEAM_MEMBER_REQUIRED_UPLOAD_COLUMNS.length === 1 ? 'is' : 'are';
  public readonly uploadTemplateUrl = TEAM_MEMBER_UPLOAD_TEMPLATE_URL;
  public isLoadingMembers = false;

  private static consolidateUploadErrorDetails(
    errorDetails: Array<{ type: string; lineNumber: number; info?: string }>,
  ): Array<{ type: string; lineNumber: number; info: string }> {
    const groupedErrors = new Map<
      string,
      { type: string; lineNumber: number; infoParts: string[] }
    >();

    for (const errorDetail of errorDetails) {
      const key = `${errorDetail.lineNumber}::${errorDetail.type}`;
      const existingError = groupedErrors.get(key);

      if (existingError) {
        if (errorDetail.info && !existingError.infoParts.includes(errorDetail.info)) {
          existingError.infoParts.push(errorDetail.info);
        }
        continue;
      }

      groupedErrors.set(key, {
        type: errorDetail.type,
        lineNumber: errorDetail.lineNumber,
        infoParts: errorDetail.info ? [errorDetail.info] : [],
      });
    }

    return Array.from(groupedErrors.values()).map((errorDetail) => ({
      type: errorDetail.type,
      lineNumber: errorDetail.lineNumber,
      info: errorDetail.infoParts.join(', '),
    }));
  }


  exportCsv(title: string | undefined): void {
    if (!title) throw new Error('Cannot export members without a workspace name');
    this.csvExport.exportTable(title, this.visibleMembers, {
      "name": {header: "Name", value: (item: any) => this.getMemberDisplayName(item)},
      "email": {header: "Email", value: (item: any) => this.getMemberDisplayEmail(item)},
      "invitation": {header: "Invitation", value: (item: any) => item.inviteAcceptedAt ? 'Accepted' : (item.inviteSentAt ? 'Invitation Sent' : 'Not Sent')},
      "wallitbalance": {header: "Wallit Balance", value: (item: any) => csvCurrency(item.wallitBalance)},
      "balance": {header: "Allowance Balance", value: (item: any) => csvCurrency(item.balance)},
      "p2pbalance": {header: "P2P Balance", value: (item: any) => csvCurrency(item.p2pBalance)},
      "employmentDate": {header: "Employment Date", value: (item: any) => csvDate(item.employmentDate, 'mediumDate')},
      "memberCreatedAt": {header: "Created", value: (item: any) => csvDate(item.memberCreatedAt, 'mediumDate')},
      "role": {header: "Role", value: (item: any) => item.role},
      "canReceiveAllowance": {header: "Allowance", value: (item: any) => item.canReceiveAllowance === 1 ? 'Enabled' : 'Disabled'},
    }, ['name','email','invitation','wallitbalance','balance','p2pbalance','employmentDate','memberCreatedAt','role','canReceiveAllowance'], {exclude: ['action', 'actionmenu']});
  }

  constructor(
    private route: ActivatedRoute,
    private teamsService: TeamsService,
    private formBuilder: UntypedFormBuilder,
    private usersService: UsersService,
    private stripeService: StripeService,
    private router: Router,
    private snackBar: MatSnackBar,
    private spinnerService: SpinnerService,
    private httpClient: HttpClient,
    private currencyPipe: CurrencyPipe,
    private sessionService: SessionService,
      private csvExport: ExporttocsvService
  ) {}

  ngOnInit(): void {
    this.isSuperAdmin = this.sessionService.isSuperAdmin();
    this.route.params.subscribe((params) => {
      if (params.teamId && params.teamId !== 'current') {
        this.teamsService.getTeamInfo(params.teamId).then((team) => {
          this.currentWellspace = team;
          this.loadTeamMembers();
          this.topupEnabled = !this.currentWellspace.topupDisabled;
        });
      } else {
        this.teamsService.wellspaceChangedObservable.subscribe((wellspace) => {
          if (wellspace) {
            this.currentWellspace = wellspace;
            this.loadTeamMembers();
            this.topupEnabled = !this.currentWellspace.topupDisabled;
          }
        });
      }
    });
    this.form = this.formBuilder.group({
      email: new UntypedFormControl('', [Validators.required, memberEmailListValidator]),
      allowance: new UntypedFormControl('', Validators.pattern('^\\d*(\\.\\d{0,2})?$')),
    });

    if (this.isSuperAdmin) {
      this.form.get('allowance')?.disable({ emitEvent: false });
    }
  }

  ngAfterViewInit(): void {
    this.emailControl.nativeElement.focus();
  }

  loadTeamMembers(): void {
    this.isLoadingMembers = true;
    this.teamsService.getTeamMembers(this.currentWellspace.id, true, true).then((members) => {
      this.members = members.filter((member) => !this.isRemovedMember(member));
      this.removedMembers = members.filter((member) => this.isRemovedMember(member));
      this.newMembers = this.teamsService.buildMemberList(this.members, this.currentWellspace);
    }).finally(() => {
      this.isLoadingMembers = false;
    });
  }

  get visibleMembers(): Array<TeamMember> {
    return this.memberView === 'removed' ? this.removedMembers : this.members;
  }

  private isRemovedMember(member: TeamMember): boolean {
    return this.isTruthyFlag(member?.teamuserMetadata?.deleted)
      || !!member?.memberDeletedAt;
  }

  private isTruthyFlag(value: unknown): boolean {
    return value === true || value === 1 || value === '1' || value === 'true';
  }

  getTeamId() {
    if (this.currentWellspace) {
      return this.currentWellspace.id;
    }
    return '';
  }

  get pendingInvitesCount(): number {
    return this.members.filter((member) => this.needsInvite(member)).length;
  }

  get hasPendingInviteAutomation(): boolean {
    return this.hasPendingAutomation(this.currentWellspace?.metadata?.inviteAutomation);
  }

  private needsInvite(member: TeamMember): boolean {
    return !!(member?.memberId || member?.id)
      && !!(member?.userEmail || member?.email)
      && !member?.inviteSentAt
      && !member?.inviteAcceptedAt;
  }

  private hasPendingAutomation(automation: any): boolean {
    return !!automation?.enabled && (
      !!automation?.initialInviteSendAt ||
      !!automation?.nextRetryAt ||
      Number(automation?.pendingInitialCount || 0) > 0 ||
      Number(automation?.pendingRetryCount || 0) > 0
    );
  }

  formatCurrency() {
    formatCurrencyInput(
      this.form.get('allowance'),
      this.allowanceControl?.nativeElement ?? null,
      this.currencyPipe,
    );
  }

  getMemberDisplayName(member: TeamMember): string {
    const firstName = member.firstName || member.teamuserMetadata?.firstName || '';
    const lastName = member.lastName || member.teamuserMetadata?.lastName || '';
    return `${firstName} ${lastName}`.trim();
  }

  getMemberDisplayEmail(member: TeamMember): string {
    return member.email || member.userEmail;
  }

  getConsolidatedUploadErrorDetails(): Array<{ type: string; lineNumber: number; info: string }> {
    return TeamAddMembersPageComponent.consolidateUploadErrorDetails(this.result?.errorDetails || []);
  }

  normalizeEmailList(value: unknown): string {
    return parseMemberEmailList(value).join('; ');
  }

  onEmailPaste(event: ClipboardEvent): void {
    const pastedValue = event.clipboardData?.getData('text') || '';
    const input = event.target as HTMLInputElement;
    const currentValue = `${this.form.get('email').value || ''}`;
    const selectionStart = input.selectionStart ?? currentValue.length;
    const selectionEnd = input.selectionEnd ?? selectionStart;
    const beforeSelection = currentValue.slice(0, selectionStart);
    const afterSelection = currentValue.slice(selectionEnd);
    const combinedValue = parseMemberEmailList(pastedValue).length > 1
      ? [beforeSelection, pastedValue, afterSelection].filter(Boolean).join('; ')
      : `${beforeSelection}${pastedValue}${afterSelection}`;

    event.preventDefault();
    this.form.get('email').setValue(this.normalizeEmailList(combinedValue));
  }

  async addMember(): Promise<void> {
    this.spinnerService.show('Loading...');
    this.uploading = true;

    try {
      const res = await this.teamsService.checkTeamPendingCharges(this.currentWellspace.id);
      if (res && res.hasPendingCharges) {
        this.snackBar.openFromComponent(SnackBarComponent, {
          data:
            'A transfer is in progress at the moment. To ensure the integrity of the operation, ' +
            'it is not possible to make further changes at this time. Please wait until the transfer is completed to proceed with your actions.',
        });
        return;
      }

      const emails = parseMemberEmailList(this.form.get('email').value);
      const allowanceForm = this.form.get('allowance').value;
      const allowance = allowanceForm ? parseFloat(allowanceForm) : undefined;
      const added: string[] = [];
      const alreadyMembers: string[] = [];
      const failed: string[] = [];

      for (const email of emails) {
        try {
          await this.teamsService.addTeamMember(this.currentWellspace.id, email, '', allowance);
          added.push(email);
        } catch (error: any) {
          if (error.status === 409) {
            alreadyMembers.push(email);
          } else {
            failed.push(email);
          }
        }
      }

      this.form.get('email').setValue(this.normalizeEmailList(failed));
      if (added.length > 0) {
        this.form.get('allowance').setValue('');
        (this.newMembers as any[]).push(...added);
        this.loadTeamMembers();
      }

      const resultParts = [
        added.length > 0 ? `${added.length} team ${added.length === 1 ? 'member' : 'members'} added` : '',
        alreadyMembers.length > 0
          ? `${alreadyMembers.length} ${alreadyMembers.length === 1 ? 'is' : 'are'} already in the workspace`
          : '',
        failed.length > 0
          ? `${failed.length} failed and ${failed.length === 1 ? 'remains' : 'remain'} in the email field`
          : '',
      ].filter(Boolean);
      this.snackBar.openFromComponent(SnackBarComponent, { data: resultParts.join('. ') });
    } catch (error: any) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: error?.error || 'Unable to add team members',
      });
    } finally {
      this.uploading = false;
      this.spinnerService.hide();
    }
  }

  async memberDeleted(member: any, dontDelete: boolean): Promise<void> {
    if (member.balance > 0) {
      this.spinnerService.show('Refunding allowance...');
      try {
        const customerId = await this.teamsService.getCustomerId(this.currentWellspace);
        await this.stripeService.issueRefund(customerId, member.balance);
        if (dontDelete) {
          await this.teamsService.modifyTeamMember(this.currentWellspace.id, member.memberId, {
            balance: 0,
          });
        }
        this.loadTeamMembers();
        this.snackBar.openFromComponent(SnackBarComponent, {
          data: `This team member has a remaining balance of $${Number(member.balance).toFixed(
            2,
          )} which will be refunded through Stripe against eligible prior charges.`,
        });
      } catch (error: any) {
        this.snackBar.openFromComponent(SnackBarComponent, {
          data: error?.data?.message || error?.message || 'Unable to refund allowance',
        });
      } finally {
        this.spinnerService.hide();
      }
    } else {
      this.loadTeamMembers();
    }
  }

  uploadMembers(data: any): void {
    this.result = [];
    this.teamsService
      .uploadMemberList(this.currentWellspace.id, data)
      .then((result) => {
        this.result = result;
        this.snackBar.openFromComponent(SnackBarComponent, {
          data: `${result.newCount} members added.`,
        });
        this.loadTeamMembers();
      })
      .catch((error) => {
        this.result = error.error ?? {};
        this.snackBar.openFromComponent(SnackBarComponent, {
          data: error.error?.errorMessage || 'Member upload failed.',
        });
      });
  }

  exportCensus(): void {
    const sheetMembers: Array<any> = this.members.map((member) => ({
      dob: new Date(
        member.birthdate ? moment(member.birthdate).format('MM/DD/YYYY') : '01/01/1978',
      ),
      sex: 'Male',
      zip: member.postalCode || '00000',
      class: 'Employee',
      type: 'Employee',
      tobacco: 'No',
      firstName: member.firstName || 'None',
      lastName: member.lastName?.split(' (')[0] || 'None',
      ftpt: 'Full Time',
      email: member.email,
    }));
    this.httpClient
      .get('/assets/files/myhealthily-census.xlsx', { responseType: 'arraybuffer' })
      .subscribe((data) => {
        const workbook = new ExcelJS.Workbook();
        workbook.xlsx.load(data).then(() => {
          const workSheet = workbook.getWorksheet('Sheet1');
          workSheet.tables = {};
          workSheet.insertRows(
            5,
            sheetMembers.map((member) => Object.values(member)),
          );
          for (let i = 5; i < 5 + sheetMembers.length; i++) {
            workSheet.getCell(i, 1).numFmt = 'mm/dd/yyyy';
          }
          const fileName = `${this.currentWellspace.name}-census.xlsx`;
          workbook.xlsx.writeBuffer().then((d: any) => {
            const blob = new Blob([d], { type: 'application/octet-stream' });
            saveAs(blob, fileName);
            this.snackBar.openFromComponent(SnackBarComponent, {
              data: `Team member census exported to ${fileName}.`,
            });
          });
        });
      });
  }
}
